import { EventFreq, IEvent, IEventRepeat } from '../../services/event-repeat'
import { ICustomOccasion, Reminder, Repeat } from '../../services/occasion-repeat'

// What the editor holds while it is being filled in, and the two conversions
// between it and a stored event. Pure: no DOM and no storage, because the
// midnights and the recurrence are the part worth testing on their own.

export type EntryKind = 'event' | 'occasion'

// The day the editor was opened on. The start date is never edited — the page
// is opened from a day, the way the occasion page is.
export interface DraftDay {
    gy: number
    gm: number
    gd: number
}

export interface Draft {
    kind: EntryKind
    occasionRepeat: Repeat
    title: string
    allDay: boolean
    startTime: string // 'HH:MM'; ignored while allDay is on
    endTime: string
    days: number // how many days it covers; 1 is a single day
    location: string
    notes: string
    freq: EventFreq
    interval: number
    until: string // 'YYYY-MM-DD', or '' for a series with no end
    reminder: Reminder
}

const pad = (n: number) => String(n).padStart(2, '0')
const clock = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

function parseClock(text: string): { h: number, m: number } {
    const [h, m] = String(text).split(':').map(Number)
    return { h: Number.isFinite(h) ? h : 0, m: Number.isFinite(m) ? m : 0 }
}

// The editor opens on a whole hour, which is what someone adding an
// appointment to a day almost always wants to change anyway.
export function emptyDraft(kind: EntryKind = 'event'): Draft {
    return {
        kind,
        occasionRepeat: 'none',
        title: '',
        allDay: false,
        startTime: '09:00',
        endTime: '10:00',
        days: 1,
        location: '',
        notes: '',
        freq: 'none',
        interval: 1,
        until: '',
        reminder: 'none',
    }
}

// How many days an event covers, counted the way the editor's stepper counts.
//
// An all-day event's end is the exclusive midnight after its last day, so the
// difference is already the number of days. A timed one is measured by the
// calendar days its two ends fall on, so an overnight event covers two.
function spanOf(e: IEvent): number {
    const first = new Date(e.start)
    const last = new Date(e.allDay ? e.end - 1 : e.end)
    const a = new Date(first.getFullYear(), first.getMonth(), first.getDate()).getTime()
    const b = new Date(last.getFullYear(), last.getMonth(), last.getDate()).getTime()
    return Math.max(1, Math.round((b - a) / 86400000) + 1)
}

export function draftFromEvent(e: IEvent): Draft {
    const start = new Date(e.start)
    const end = new Date(e.end)
    return {
        kind: 'event',
        occasionRepeat: 'none',
        title: e.title,
        allDay: e.allDay,
        startTime: clock(start),
        endTime: clock(end),
        days: spanOf(e),
        location: e.location || '',
        notes: e.notes || '',
        freq: e.repeat.freq,
        interval: Math.max(1, e.repeat.interval || 1),
        until: e.repeat.until === undefined ? '' : isoDay(new Date(e.repeat.until)),
        reminder: e.reminder,
    }
}

function repeatOf(d: Draft): IEventRepeat {
    if (d.freq === 'none') return { freq: 'none', interval: 1 }
    const repeat: IEventRepeat = { freq: d.freq, interval: Math.max(1, Math.round(d.interval) || 1) }
    if (d.until) {
        const [y, m, day] = d.until.split('-').map(Number)
        if (y && m && day) repeat.until = new Date(y, m - 1, day).getTime()
    }
    return repeat
}

export function eventFromDraft(d: Draft, day: DraftDay): Omit<IEvent, 'id'> {
    const span = Math.max(1, Math.round(d.days) || 1)
    let start: number
    let end: number

    if (d.allDay) {
        start = new Date(day.gy, day.gm - 1, day.gd).getTime()
        // Exclusive: the midnight after the last day it covers. Keeping it
        // exclusive is what lets one day and three days be measured the same
        // way, here and in the day expansion the display uses.
        end = new Date(day.gy, day.gm - 1, day.gd + span).getTime()
    } else {
        const from = parseClock(d.startTime)
        const to = parseClock(d.endTime)
        start = new Date(day.gy, day.gm - 1, day.gd, from.h, from.m).getTime()
        end = new Date(day.gy, day.gm - 1, day.gd + span - 1, to.h, to.m).getTime()
        // An end at or before the start is how someone writes an event that
        // runs past midnight, so it is read as the next day rather than
        // refused.
        if (end <= start) end = new Date(day.gy, day.gm - 1, day.gd + span, to.h, to.m).getTime()
    }

    const out: Omit<IEvent, 'id'> = {
        title: d.title.trim(),
        allDay: d.allDay,
        start,
        end,
        repeat: repeatOf(d),
        reminder: d.reminder,
    }
    if (d.location.trim()) out.location = d.location.trim()
    if (d.notes.trim()) out.notes = d.notes.trim()
    return out
}

// A title is the one thing an event cannot do without — the same bar the
// occasion wizard sets before it will let you past its first step.
export const canSave = (d: Draft) => d.title.trim().length > 0

export const draftFromOccasion = (o: ICustomOccasion): Draft => ({
    ...emptyDraft('occasion'),
    title: o.title,
    occasionRepeat: o.repeat,
    reminder: o.reminder,
})
